import type { FieldDefinition } from "@estelle/domain";

import { ORDER_FIELDS } from "@/modules/orders/fields";

/**
 * What Reports > Orders' chart can be filtered by (#449): every order field,
 * the same vocabulary and the same filter builder the Orders list uses, so
 * whatever narrows a list narrows the chart.
 *
 * One field is worded differently here. RTO — a parcel returned to origin — has
 * no tag worth filtering on: `rto` is on three orders of 572 on the live store.
 * What marks it is the shipment's delivery status, where `not_delivered` is on
 * sixty. So the RTO filter is the existing delivery-status field, with that one
 * value named for what the people reading a report call it. The Orders list
 * keeps Shopify's own wording ("Not delivered", #350).
 *
 * Until the real shipment stage lands (#426), `not_delivered` can also hold a
 * parcel that was lost rather than returned — which is why the label says it is
 * the RTO status, not that the two are the same.
 */
export const ORDER_CHART_FIELDS: FieldDefinition[] = ORDER_FIELDS.map(
  (field) =>
    field.name === "deliveryStatus"
      ? {
          ...field,
          options: field.options?.map((option) =>
            option.value === "not_delivered"
              ? { ...option, label: "Not delivered (RTO)" }
              : option,
          ),
        }
      : field,
);

/**
 * What the damaged-and-missing returns report can be filtered by (#529): one row
 * per receipt line and outcome, so a line with a damaged unit and a missing one
 * is two rows, each with its own reason.
 *
 * `photo` is shown and not filtered: a count of photos is not something anyone
 * asks the list, but a report row of a damaged item with no way to see it would
 * miss the point of requiring one.
 */
export const RETURN_LOSS_FIELDS: FieldDefinition[] = [
  { name: "receivedAt", label: "Received", type: "datetime", sortable: true },
  { name: "orderNumber", label: "Order", type: "text", sortable: true },
  {
    name: "outcome",
    label: "Outcome",
    type: "enum",
    sortable: true,
    options: [
      { value: "damaged", label: "Damaged" },
      { value: "missing", label: "Missing" },
    ],
  },
  { name: "item", label: "Item", type: "text", sortable: true },
  { name: "sku", label: "SKU", type: "text", sortable: true },
  { name: "quantity", label: "Quantity", type: "number", sortable: true },
  { name: "reason", label: "Reason", type: "text", sortable: true },
  { name: "photo", label: "Photo", type: "text", filterable: false },
  { name: "receivedBy", label: "Received by", type: "text", sortable: true },
];
